import { AlertTriangle, Lock, Plus, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { serviceKey } from '@obraplan/core/domain/takeoff/service-catalog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/cn';
import type { OfertaDaDisciplina, OfertaDoServico } from './escopo-da-tipologia';
import { aprovacaoDe, textoDaAprovacao, type ServicosAprovados } from './servicos-aprovados';

/**
 * O que a tipologia usa, escolhido de uma vez.
 *
 * O catálogo da obra é biblioteca: os serviços dela e os ~150 globais. O painel mostra só
 * as disciplinas que a estrutura cadastrou nos andares desta tipologia — pintura de
 * fachada não aparece para escolher no apartamento se o andar não tem pintura —, e dentro
 * delas o que se marca é o que a tipologia passa a mostrar em todas as telas.
 *
 * Desmarcar um serviço que já tem número pede confirmação antes de gravar: o que foi
 * digitado nele sai junto. O que veio da planilha não sai — é da planilha —, e volta se o
 * serviço for marcado de novo.
 */
export interface EscolherServicosProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tipologia: string;
  oferta: readonly OfertaDaDisciplina[];
  /** O que está valendo hoje — gravado, ou proposto a partir das quantidades. */
  escolhidos: ReadonlySet<string>;
  /** A escolha de hoje foi gravada por alguém, ou é só o ponto de partida? */
  gravada: boolean;
  salvando: boolean;
  erro?: string | undefined;
  /** Sem permissão de editar o quantitativo: o painel mostra, e não muda. */
  somenteLeitura?: boolean;
  /** `apagar` são os desmarcados que tinham número — quem chama limpa o digitado neles. */
  onSalvar: (escolhidos: readonly string[], apagar: readonly string[]) => void;
  /** Cria o serviço na obra. Devolve a chave dele, para o painel já marcá-lo. */
  onCriar?: (disciplina: { key: string; name: string }, nome: string) => void;
  criando?: boolean;
  /**
   * Os serviços aprovados no orçamento (Fase 33, RN-438): a caixa deles fica como está — pôr ou
   * tirar o aprovado da tipologia mudaria a quantidade dele, e o servidor recusaria.
   */
  aprovados?: ServicosAprovados | undefined;
}

export function EscolherServicos({
  open,
  onOpenChange,
  tipologia,
  oferta,
  escolhidos,
  gravada,
  salvando,
  erro,
  somenteLeitura = false,
  onSalvar,
  onCriar,
  criando = false,
  aprovados,
}: EscolherServicosProps) {
  const travado = (chave: string) => aprovacaoDe(aprovados, chave) !== undefined;
  const [marcados, setMarcados] = useState<ReadonlySet<string>>(new Set());
  const [busca, setBusca] = useState('');
  const [confirmando, setConfirmando] = useState(false);
  const [novaDisciplina, setNovaDisciplina] = useState('');
  const [novoNome, setNovoNome] = useState('');

  // Reabrir o painel recomeça do que está valendo — e não do rascunho de quem cancelou.
  //
  // Só na abertura: `escolhidos` e `oferta` mudam com o painel aberto — o serviço criado
  // aqui chega ao catálogo e depois ao servidor —, e recomeçar a cada mudança apagaria o
  // que a pessoa acabou de marcar.
  useEffect(() => {
    if (!open) return;
    setMarcados(new Set(escolhidos));
    setBusca('');
    setConfirmando(false);
    setNovoNome('');
    setNovaDisciplina(oferta[0]?.key ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const procurado = busca.trim().toLocaleLowerCase('pt-BR');
  const visiveis = useMemo(
    () =>
      oferta
        .map((disciplina) => ({
          ...disciplina,
          servicos:
            procurado === ''
              ? disciplina.servicos
              : disciplina.servicos.filter((oferta) =>
                  `${disciplina.name} ${oferta.servico.name}`
                    .toLocaleLowerCase('pt-BR')
                    .includes(procurado),
                ),
        }))
        .filter((disciplina) => procurado === '' || disciplina.servicos.length > 0),
    [oferta, procurado],
  );

  const todas = oferta.flatMap((disciplina) => disciplina.servicos);
  const totalMarcados = todas.filter((item) => marcados.has(item.servico.key)).length;
  /** Tinham número e foram desmarcados agora — é o que a confirmação lista. */
  const perdem = todas.filter(
    (item) =>
      item.temQuantidade && escolhidos.has(item.servico.key) && !marcados.has(item.servico.key),
  );

  const alternar = (chave: string, marcar: boolean) =>
    setMarcados((atual) => {
      if (travado(chave)) return atual;
      const proximo = new Set(atual);
      if (marcar) proximo.add(chave);
      else proximo.delete(chave);
      return proximo;
    });

  const alternarDisciplina = (servicos: readonly OfertaDoServico[], marcar: boolean) =>
    setMarcados((atual) => {
      const proximo = new Set(atual);
      for (const { servico } of servicos) {
        // O aprovado fica como está, também no "marcar todos".
        if (travado(servico.key)) continue;
        if (marcar) proximo.add(servico.key);
        else proximo.delete(servico.key);
      }
      return proximo;
    });

  const salvar = () =>
    onSalvar(
      [...marcados],
      perdem.map((item) => item.servico.key),
    );

  const disciplinaNova = oferta.find((disciplina) => disciplina.key === novaDisciplina);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Serviços de {tipologia}</DialogTitle>
          <DialogDescription>
            Só aparecem as disciplinas cadastradas na Estrutura para os andares desta tipologia. O
            que estiver marcado é o que as abas mostram, lançam e somam.
          </DialogDescription>
        </DialogHeader>

        {confirmando ? (
          <div className="flex flex-col gap-3">
            <p className="flex items-start gap-2 text-sm">
              <AlertTriangle
                className="mt-0.5 size-4 shrink-0 text-(--color-state-warn)"
                aria-hidden="true"
              />
              <span>
                {perdem.length === 1
                  ? 'Este serviço tem quantidade nesta tipologia.'
                  : `Estes ${String(perdem.length)} serviços têm quantidade nesta tipologia.`}{' '}
                O que foi <strong>digitado</strong> neles será apagado; o que veio da planilha volta
                se você marcá-los de novo.
              </span>
            </p>
            <ul className="flex flex-col divide-y divide-(--color-border-subtle) rounded-md border border-(--color-border-subtle) text-sm">
              {perdem.map((item) => (
                <li key={item.servico.key} className="px-3 py-2">
                  {item.servico.name}
                  <span className="text-(--color-content-muted)">
                    {' '}
                    · {item.servico.items.length}{' '}
                    {item.servico.items.length === 1 ? 'item' : 'itens'}
                  </span>
                </li>
              ))}
            </ul>
            <DialogFooter>
              <Button variant="secondary" onClick={() => setConfirmando(false)}>
                Voltar
              </Button>
              <Button variant="danger" disabled={salvando} onClick={salvar}>
                {salvando ? 'Salvando…' : 'Apagar e salvar'}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {!gravada && escolhidos.size > 0 && (
              <p className="rounded-md border border-(--color-state-info) px-3 py-2 text-sm">
                Ponto de partida: os serviços que já têm quantidade nesta tipologia. Nada foi
                gravado ainda — salve para fixar a escolha.
              </p>
            )}

            {oferta.length === 0 ? (
              <p className="rounded-md border border-(--color-state-warn) px-3 py-2 text-sm">
                Os andares desta tipologia não têm nenhuma disciplina cadastrada na Estrutura.
                Cadastre as disciplinas do pavimento lá, e elas aparecem aqui.
              </p>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="relative min-w-56 flex-1">
                    <Search
                      className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-(--color-content-muted)"
                      aria-hidden="true"
                    />
                    <Input
                      aria-label="Procurar serviço"
                      placeholder="Procurar serviço…"
                      className="pl-9"
                      value={busca}
                      onChange={(event) => setBusca(event.target.value)}
                    />
                  </div>
                  <span className="tabular text-sm text-(--color-content-muted)" aria-live="polite">
                    {totalMarcados} de {todas.length} marcados
                  </span>
                </div>

                <div className="flex max-h-[26rem] flex-col gap-4 overflow-y-auto pr-1">
                  {visiveis.map((disciplina) => (
                    <DisciplinaDoPainel
                      key={disciplina.key}
                      disciplina={disciplina}
                      marcados={marcados}
                      somenteLeitura={somenteLeitura}
                      aprovados={aprovados}
                      onAlternar={alternar}
                      onAlternarTodos={alternarDisciplina}
                    />
                  ))}
                  {visiveis.length === 0 && (
                    <p className="text-sm text-(--color-content-muted)">
                      Nenhum serviço corresponde à busca.
                    </p>
                  )}
                </div>

                {onCriar !== undefined && !somenteLeitura && (
                  <form
                    className="flex flex-col gap-2 rounded-md border border-dashed border-(--color-border-subtle) p-3"
                    onSubmit={(event) => {
                      event.preventDefault();
                      const nome = novoNome.trim();
                      if (disciplinaNova === undefined || nome === '') return;
                      onCriar({ key: disciplinaNova.key, name: disciplinaNova.name }, nome);
                      alternar(serviceKey(disciplinaNova.name, nome), true);
                      setNovoNome('');
                    }}
                  >
                    <p className="text-sm font-medium">Não achou? Criar serviço nesta obra</p>
                    <div className="flex flex-wrap items-center gap-2">
                      <select
                        aria-label="Disciplina do serviço novo"
                        className="h-11 rounded-md border border-(--color-border-subtle) bg-(--color-surface) px-2 text-sm md:h-9"
                        value={novaDisciplina}
                        onChange={(event) => setNovaDisciplina(event.target.value)}
                      >
                        {oferta.map((disciplina) => (
                          <option key={disciplina.key} value={disciplina.key}>
                            {disciplina.name}
                          </option>
                        ))}
                      </select>
                      <Input
                        aria-label="Nome do serviço novo"
                        placeholder="Nome do serviço"
                        className="min-w-48 flex-1"
                        value={novoNome}
                        onChange={(event) => setNovoNome(event.target.value)}
                      />
                      <Button
                        type="submit"
                        variant="secondary"
                        disabled={criando || novoNome.trim() === ''}
                      >
                        <Plus aria-hidden="true" />
                        {criando ? 'Criando…' : 'Criar e marcar'}
                      </Button>
                    </div>
                  </form>
                )}
              </>
            )}

            {erro !== undefined && (
              <p role="alert" className="text-sm text-(--color-state-danger)">
                {erro}
              </p>
            )}

            <DialogFooter>
              <Button variant="secondary" onClick={() => onOpenChange(false)}>
                {somenteLeitura ? 'Fechar' : 'Cancelar'}
              </Button>
              {!somenteLeitura && (
                <Button
                  variant="primary"
                  disabled={salvando || oferta.length === 0}
                  onClick={() => (perdem.length > 0 ? setConfirmando(true) : salvar())}
                >
                  {salvando ? 'Salvando…' : 'Salvar escolha'}
                </Button>
              )}
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DisciplinaDoPainel({
  disciplina,
  marcados,
  somenteLeitura,
  aprovados,
  onAlternar,
  onAlternarTodos,
}: {
  disciplina: OfertaDaDisciplina;
  marcados: ReadonlySet<string>;
  somenteLeitura: boolean;
  aprovados: ServicosAprovados | undefined;
  onAlternar: (chave: string, marcar: boolean) => void;
  onAlternarTodos: (servicos: readonly OfertaDoServico[], marcar: boolean) => void;
}) {
  const quantos = disciplina.servicos.filter((item) => marcados.has(item.servico.key)).length;
  const todos = disciplina.servicos.length > 0 && quantos === disciplina.servicos.length;

  return (
    <fieldset className="flex flex-col gap-1">
      <legend className="flex w-full items-center gap-3">
        <label className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-(--color-content-muted)">
          <input
            type="checkbox"
            className="size-4"
            checked={todos}
            ref={(input) => {
              // "Alguns" não é marcado nem desmarcado: o traço diz isso sem precisar de cor.
              if (input !== null) input.indeterminate = quantos > 0 && !todos;
            }}
            disabled={somenteLeitura || disciplina.servicos.length === 0}
            aria-label={`Marcar todos os serviços de ${disciplina.name}`}
            onChange={(event) => onAlternarTodos(disciplina.servicos, event.target.checked)}
          />
          {disciplina.name}
          <span className="tabular font-medium normal-case tracking-normal">
            · {quantos} de {disciplina.servicos.length}
          </span>
        </label>
        <span className="h-px flex-1 bg-(--color-border-subtle)" aria-hidden="true" />
      </legend>

      {disciplina.servicos.length === 0 ? (
        <p className="pl-6 text-sm text-(--color-content-muted)">
          Nenhum serviço cadastrado nesta disciplina — crie o primeiro abaixo.
        </p>
      ) : (
        <ul className="grid gap-x-4 gap-y-0.5 pl-6 sm:grid-cols-2">
          {disciplina.servicos.map((item) => {
            const aprovado = aprovacaoDe(aprovados, item.servico.key);
            return (
              <li key={item.servico.key}>
                <label
                  className={cn(
                    'flex min-h-9 items-center gap-2 rounded-md px-1 text-sm',
                    !somenteLeitura &&
                      aprovado === undefined &&
                      'cursor-pointer hover:bg-(--color-surface-muted)',
                  )}
                  {...(aprovado === undefined
                    ? {}
                    : {
                        title: `Serviço ${textoDaAprovacao(aprovado)}: ele não entra nem sai da tipologia.`,
                      })}
                >
                  <input
                    type="checkbox"
                    className="size-4 shrink-0"
                    checked={marcados.has(item.servico.key)}
                    disabled={somenteLeitura || aprovado !== undefined}
                    onChange={(event) => onAlternar(item.servico.key, event.target.checked)}
                  />
                  <span className="min-w-0 flex-1 truncate">{item.servico.name}</span>
                  {aprovado !== undefined && (
                    <Badge tone="ok" icon={<Lock className="size-3" aria-hidden="true" />}>
                      aprovado
                    </Badge>
                  )}
                  {item.temQuantidade && <Badge tone="info">tem quantidade</Badge>}
                  {item.desativado && <Badge tone="neutral">desativado</Badge>}
                  {item.id === undefined && <Badge tone="warn">gravando…</Badge>}
                </label>
              </li>
            );
          })}
        </ul>
      )}
    </fieldset>
  );
}
